import type {
  MessageAAD,
  MessageCryptoProvider,
  MessageEnvelopeAny,
} from './messageCryptoTypes';
import {
  createDevPlainEnvelopeForWeb,
  decodeDevPlainEnvelope,
  normalizeEnvelopeLike,
} from './devPlainCompat';

let sodiumPromise: Promise<any> | null = null;

async function getSodium() {
  if (!sodiumPromise) {
    sodiumPromise = import('libsodium-wrappers-sumo').then(async (mod: any) => {
      const sodium = mod.default || mod;
      await sodium.ready;
      return sodium;
    });
  }

  return sodiumPromise;
}

function shouldSendV2() {
  try {
    return window.localStorage.getItem('intimsync.messages.crypto.send_v2') === '1';
  } catch {
    return false;
  }
}

function serializeAAD(aad?: MessageAAD) {
  /**
   * TEMPORARY DEV AAD.
   *
   * Keep this stable across sender/recipient rows while the backend still
   * fans out/stores rows in its current shape. Binding to clientMessageId or
   * senderUserId is safer later, but it caused dev v2 decrypt mismatches
   * because backend-returned row metadata is not guaranteed to match the exact
   * sender-side AAD yet.
   */
  if (!aad) return '';

  return [
    `conversationId=${aad.conversationId}`,
    `version=2`,
  ].join('\\n');
}

async function sha256Bytes(input: string): Promise<Uint8Array> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return new Uint8Array(digest);
}

async function deriveTemporaryConversationKey(aad?: MessageAAD): Promise<Uint8Array> {
  /**
   * TEMPORARY DEV KEY.
   *
   * This is only for web v2 crypto smoke-testing before real device keys and
   * per-recipient wrapped conversation keys are added.
   *
   * Production replacement:
   * - local device identity key
   * - conversation content key
   * - per-recipient device key wrapping
   * - key rotation
   */
  const conversationId = aad?.conversationId || 'global';
  return sha256Bytes(`intimsync-dev-message-xchacha20-key-v1:${conversationId}`);
}

class MessageCryptoProviderV2 implements MessageCryptoProvider {
  async encryptText(args: {
    plaintext: string;
    aad: MessageAAD;
    keyRef?: string;
  }): Promise<MessageEnvelopeAny> {
    if (!shouldSendV2()) {
      return createDevPlainEnvelopeForWeb(args.plaintext);
    }

    const sodium = await getSodium();

    const key = await deriveTemporaryConversationKey(args.aad);
    const nonce = sodium.randombytes_buf(
      sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES
    );

    const plaintextBytes = sodium.from_string(args.plaintext);
    const aadBytes = sodium.from_string(serializeAAD(args.aad));

    const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
      plaintextBytes,
      aadBytes,
      null,
      nonce,
      key
    );

    return {
      version: 2,
      algorithm: 'XCHACHA20-POLY1305',
      ciphertext: sodium.to_base64(ciphertext, sodium.base64_variants.ORIGINAL),
      nonce: sodium.to_base64(nonce, sodium.base64_variants.ORIGINAL),
      senderKeyId: args.keyRef || `web-dev-xchacha20:${args.aad.conversationId}`,
    };
  }

  async decryptText(args: {
    envelope: MessageEnvelopeAny;
    aad?: MessageAAD;
    keyRef?: string;
  }): Promise<string> {
    const raw: any = args.envelope || {};
    const normalized: any = normalizeEnvelopeLike({ envelope: raw } as any);

    const ciphertext = String(
      normalized.ciphertext ||
      raw.ciphertext ||
      raw.cipherText ||
      raw.body_ciphertext ||
      ''
    );

    const nonceText = String(
      normalized.nonce ||
      raw.nonce ||
      raw.iv ||
      raw.body_nonce ||
      ''
    );

    const algorithm = String(
      normalized.algorithm ||
      raw.algorithm ||
      ''
    ).toUpperCase();

    const senderKeyId = String(
      normalized.senderKeyId ||
      raw.senderKeyId ||
      raw.sender_key_id ||
      ''
    );

    const version = Number(
      normalized.version ||
      raw.version ||
      raw.crypto_version ||
      0
    );

    const devPlain = decodeDevPlainEnvelope({
      ...normalized,
      ciphertext,
      nonce: nonceText,
      algorithm,
      senderKeyId,
      version,
    });
    if (devPlain) return devPlain;

    if (!ciphertext || !nonceText) return '';

    const sodium = await getSodium();

    let nonce: Uint8Array;
    let cipherBytes: Uint8Array;

    try {
      nonce = sodium.from_base64(nonceText, sodium.base64_variants.ORIGINAL);
      cipherBytes = sodium.from_base64(ciphertext, sodium.base64_variants.ORIGINAL);
    } catch {
      return '';
    }

    const looksLikeV2 =
      version === 2 ||
      algorithm.includes('XCHACHA') ||
      senderKeyId.toLowerCase().includes('xchacha') ||
      nonce.length === sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES;

    if (!looksLikeV2) return '';

    const stableConversationAAD: MessageAAD | undefined = args.aad?.conversationId
      ? {
          conversationId: args.aad.conversationId,
          clientMessageId: '',
          senderUserId: '',
          recipientScope: '',
          version: 2,
        }
      : undefined;

    const aadCandidates: Array<MessageAAD | undefined> = [
      stableConversationAAD,
      args.aad,
      undefined,
    ];

    const seen = new Set<string>();

    for (const aadCandidate of aadCandidates) {
      const aadText = serializeAAD(aadCandidate);
      if (seen.has(aadText)) continue;
      seen.add(aadText);

      try {
        const key = await deriveTemporaryConversationKey(args.aad);
        const aadBytes = sodium.from_string(aadText);

        const plaintextBytes = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
          null,
          cipherBytes,
          aadBytes,
          nonce,
          key
        );

        return sodium.to_string(plaintextBytes);
      } catch {
        // Try next AAD candidate.
      }
    }

    try {
      console.warn('[Messages crypto] v2 decrypt failed', {
        version,
        algorithm,
        senderKeyId,
        nonceBytes: nonce.length,
        ciphertextBytes: cipherBytes.length,
        aadConversationId: args.aad?.conversationId || '',
      });
    } catch {}

    return '';
  }
}

let provider: MessageCryptoProvider | null = null;

export function getMessageCryptoProvider(): MessageCryptoProvider {
  if (!provider) provider = new MessageCryptoProviderV2();
  return provider;
}
